import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CheckCircle2, Star, ArrowRight } from "lucide-react";

const PricingSection = () => {
  const pricingPlans = [
    {
      id: "regular",
      name: "Regular Clean",
      description: "Perfect for ongoing maintenance",
      startingPrice: "15",
      unit: "per room",
      features: [
        "Living room from £20",
        "Bedroom from £15",
        "Bathroom from £20",
        "Kitchen from £25",
        "Weekly/Fortnightly/Monthly",
        "Consistent cleaning team",
      ],
      cta: "Get Quote",
      popular: false,
    },
    {
      id: "detailed",
      name: "Detailed Clean",
      description: "Our signature deep cleaning service",
      startingPrice: "25",
      unit: "per room",
      features: [
        "All Regular Clean services",
        "Window sills & tracks",
        "Skirting boards",
        "Light switches sanitised",
        "Inside microwave",
        "Cupboard fronts cleaned",
      ],
      cta: "Get Quote",
      popular: true,
    },
    {
      id: "deep",
      name: "Deep Clean",
      description: "Complete transformation clean",
      startingPrice: "Contact",
      unit: "for quote",
      features: [
        "All Detailed Clean services",
        "Inside oven & fridge",
        "Descaling showers/taps",
        "Interior windows",
        "Carpet spot treatment",
        "Wall marks removal",
      ],
      cta: "Get Quote",
      popular: false,
    },
  ];

  const scrollToQuote = () => {
    const element = document.querySelector("#quote");
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="pricing"
      data-testid="pricing-section"
      className="py-20 lg:py-32 bg-[#F8FAFC] relative overflow-hidden"
    >
      {/* Background decoration */}
      <div className="absolute top-20 left-0 w-72 h-72 blue-glow rounded-full blur-3xl opacity-30" />
      <div className="absolute bottom-20 right-0 w-96 h-96 blue-glow rounded-full blur-3xl opacity-20" />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
        {/* Section Header */}
        <div className="text-center mb-16 animate-fade-in-up">
          <div className="inline-flex items-center gap-2 bg-white border border-[#3B82F6]/20 rounded-full px-4 py-2 mb-6 shadow-sm">
            <span className="text-sm font-medium text-[#3B82F6]">Transparent Pricing</span>
          </div>
          <h2
            className="text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0F172A] mb-4 tracking-tight"
            style={{ fontFamily: "Manrope, sans-serif" }}
          >
            Simple, Fair Pricing
          </h2>
          <p className="text-lg text-[#64748B] max-w-2xl mx-auto">
            Prices based on room size. Minimum booking 2-3 hours typically. Commercial quotes available.
          </p>
        </div>

        {/* Pricing Cards */}
        <div className="grid md:grid-cols-3 gap-8">
          {pricingPlans.map((plan, index) => (
            <Card
              key={plan.id}
              data-testid={`pricing-card-${plan.id}`}
              className={`relative overflow-hidden border transition-all duration-300 card-hover tracing-beam ${
                plan.popular
                  ? "border-[#3B82F6] shadow-xl scale-105 z-10 bg-white"
                  : "border-slate-100 shadow-sm bg-white hover:border-[#3B82F6]/30"
              }`}
            >
              {plan.popular && (
                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#3B82F6] via-[#60A5FA] to-[#3B82F6]" />
              )}
              
              <CardHeader className="pb-4 pt-8">
                {plan.popular && (
                  <div className="inline-flex items-center gap-1 bg-[#EFF6FF] text-[#3B82F6] text-xs font-semibold px-3 py-1 rounded-full mb-4 w-fit">
                    <Star className="w-3 h-3" fill="currentColor" />
                    Most Popular
                  </div>
                )}
                <CardTitle 
                  className="text-2xl font-bold text-[#0F172A]"
                  style={{ fontFamily: "Manrope, sans-serif" }}
                >
                  {plan.name}
                </CardTitle>
                <p className="text-[#64748B] text-sm">{plan.description}</p>
              </CardHeader>

              <CardContent className="space-y-6">
                {/* Price */}
                <div className="flex items-baseline gap-1">
                  {plan.startingPrice !== "Contact" && (
                    <span className="text-sm text-[#64748B]">from</span>
                  )}
                  <span 
                    className="text-4xl font-bold text-[#0F172A]"
                    style={{ fontFamily: "Manrope, sans-serif" }}
                  >
                    {plan.startingPrice === "Contact" ? "" : "£"}{plan.startingPrice}
                  </span>
                  <span className="text-[#64748B] text-sm">{plan.unit}</span>
                </div>

                {/* Features */}
                <ul className="space-y-3">
                  {plan.features.map((feature) => (
                    <li key={feature} className="flex items-start gap-3 text-sm text-[#64748B]">
                      <CheckCircle2 className="w-5 h-5 text-[#3B82F6] flex-shrink-0" />
                      <span>{feature}</span>
                    </li>
                  ))}
                </ul>

                {/* CTA */}
                <Button
                  onClick={scrollToQuote}
                  className={`w-full rounded-full py-6 transition-all duration-300 btn-shine ${
                    plan.popular
                      ? "bg-[#3B82F6] hover:bg-[#2563EB] text-white shadow-lg hover:shadow-xl"
                      : "bg-[#0F172A] hover:bg-[#1E293B] text-white"
                  }`}
                  data-testid={`pricing-cta-${plan.id}`}
                >
                  {plan.cta}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Bottom note */}
        <div className="text-center mt-12 animate-fade-in-up">
          <p className="text-[#64748B]">
            Need a custom quote for your business?{" "}
            <button 
              onClick={scrollToQuote}
              className="text-[#3B82F6] font-medium hover:underline"
              data-testid="commercial-quote-link"
            >
              Contact us for commercial rates
            </button>
          </p>
        </div>
      </div>
    </section>
  );
};

export default PricingSection;
